import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Feature } from '../../models/Feature'
import { RecordFactory } from '../../models/RecordFactory'
import { apiClient } from '../../modules/apiclient'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { FullScreenVideoModal } from '../app/FullScreenVideoModal'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import SegmentedControl from '../app/design/SegmentedControl'
import { Clickable } from '../common/Clickable'
import { ListHeader } from '../common/ListHeader'

type Tutorial = {
    title: string
    subtitle: string
    id: string
    icon: React.ReactNode
    videoUrl?: string
    playInModal: boolean
}

const guidedSetupList: Tutorial[] = [
    {
        icon: <Icon icon="hand.large" background="round" className="size-12 shrink-0" />,
        title: 'Welcome to PocketSuite',
        subtitle:
            'Learn where to find powerful PocketSuite features, tutorial videos & how-to articles, and live support.',
        id: 'guided_setup_welcome',
        playInModal: false,
    },
    {
        icon: <Icon icon="student.large" background="round" className="size-12 shrink-0" />,
        title: 'PocketSuite Foundations',
        subtitle:
            'Settings, scheduling, online booking, client fields, forms, contracts, cancellations, refunds & more!',
        id: 'guided_setup_foundations',
        playInModal: false,
    },
    {
        icon: <Icon icon="trending.large" background="round" className="size-12 shrink-0" />,
        title: 'PocketSuite growth lab',
        subtitle: 'Packages, subscriptions, gift certificates, smart campaigns, suite link & more!',
        id: 'guided_setup_growthlab',
        playInModal: false,
    },
]

const viewWillAppear = async (base: PocketComponent<void>) => {
    const guidedTutorials = await base.queryClient.fetchQuery({
        queryKey: ['guided_tutorials'],
        queryFn: async () => {
            return await Promise.all(
                guidedSetupList.map(async (t) => {
                    const result = await apiClient.post<{ tutorial_uri: string }>(
                        `/tutorial/${t.id}`
                    )
                    return {
                        ...t,
                        videoUrl: result.data.tutorial_uri,
                    }
                })
            )
        },
    })

    const featuresFromCache = await RecordFactory.getInstance().loadFeatures()

    const otherStartingTutorials = [
        Feature.initWithId('overview'),
        Feature.initWithId('setup'),
        Feature.initWithId('linked_accounts'),
        Feature.initWithId('reports'),
        Feature.initWithId('fields'),
        Feature.initWithId('calendar'),
        Feature.initWithId('documents'),
        base.me.owner.allowTask && base.me.owner.allowReservation && Feature.initWithId('actions'),
        (base.me.solo || base.me.company || base.me.payee) && Feature.initWithId('conference'),
        featuresFromCache.find((f) => f.id === 'contract') && Feature.initWithId('imports'),
        featuresFromCache.find((f) => f.id === 'booking') && Feature.initWithId('cancellations'),
    ].filter((item): item is Feature => Boolean(item))

    const industry = base.owner.industry
    const featuresToDisplay = [...otherStartingTutorials, ...featuresFromCache].filter((f) => {
        if (
            !f.id ||
            (base.me.type === 'payee' && ['benchmark', 'staffing', 'trigger'].includes(f.id))
        ) {
            return false
        }

        if (Feature.enabled(f.feature, base.owner)) {
            return true
        }

        return !!industry && f.allowed(industry)
    })

    const features = await Promise.all(
        featuresToDisplay.map(async (f) => ({
            title: f.name ?? '',
            subtitle: f.memo || '',
            id: f.id ?? '',
            icon: (
                <Icon
                    icon={`${f.iconForOwner(base.me)}.large`}
                    background="round"
                    className="size-12 shrink-0"
                />
            ),
            videoUrl: await base.queryClient.fetchQuery({
                queryKey: ['tutorial', f.id],
                queryFn: async () => {
                    const result = await apiClient.post<{ video_uri: string }>(
                        `/tutorial/${f.tutorial}`
                    )
                    return result.data.video_uri ?? ''
                },
            }),
            playInModal: true,
        }))
    )

    return { guidedTutorials, features }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const [view, setView] = useState<'guided_setup' | 'tutorial'>('guided_setup')
    const [searchParams, setSearchParams] = useSearchParams()
    const selectedTutorialId = searchParams.get('tutorial')
    const selectedTutorial = data.features.find((f) => f.id === selectedTutorialId)

    const tutorials: Tutorial[] = useMemo(() => {
        if (view === 'guided_setup') {
            return data.guidedTutorials
        }

        return data.features
    }, [view, data.features])

    return (
        <>
            <div className="mx-auto max-w-[calc(744px+64px)] p-8 pt-6">
                <ListHeader
                    title="Tutorials"
                    backButton={{
                        label: 'Settings',
                        href: '/settings',
                    }}
                />
                <div className="mx-auto mt-6 w-[744px] rounded-lg bg-white p-8">
                    <div className="mb-8 flex w-full justify-center">
                        <SegmentedControl
                            size="small"
                            segments={[
                                { label: 'Guided Setup', value: 'guided_setup' },
                                { label: 'Tutorials', value: 'tutorial' },
                            ]}
                            className="w-full"
                            value={view}
                            onChange={(v) => setView(v as 'guided_setup' | 'tutorial')}
                        />
                    </div>

                    <div className="flex flex-col gap-y-4">
                        {tutorials.map((t) => {
                            return (
                                <Clickable
                                    key={t.id}
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (t.playInModal) {
                                            setSearchParams({ tutorial: t.id })
                                        }
                                        else {
                                            window.open(t.videoUrl, '_blank')
                                        }
                                    }}
                                    className="flex items-center justify-between rounded-lg p-4 hover:bg-black/4 active:bg-black/8"
                                >
                                    <div className="flex items-center gap-x-4">
                                        {t.icon}
                                        <div className="flex flex-col justify-start gap-y-1 text-left">
                                            <Label size="md" className="text-gray-700">
                                                {t.title}
                                            </Label>
                                            <Paragraph size="sm" className="text-gray-600">
                                                {t.subtitle}
                                            </Paragraph>
                                        </div>
                                    </div>

                                    {t.playInModal ? (
                                        <Button
                                            intent="tertiaryLink"
                                            size="small"
                                            rightIcon="chevronRight.large"
                                            className="text-gray-500"
                                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                if (t.playInModal) {
                                                    setSearchParams({ tutorial: t.id })
                                                }
                                                else {
                                                    window.open(t.videoUrl, '_blank')
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Button
                                            intent="tertiaryLink"
                                            size="small"
                                            rightIcon="externalLink.small"
                                            className="whitespace-nowrap"
                                        >
                                            Play video
                                        </Button>
                                    )}
                                </Clickable>
                            )
                        })}
                    </div>
                </div>
            </div>
            <FullScreenVideoModal
                isOpen={!!selectedTutorial}
                onClose={() => setSearchParams({})}
                hlsUrl={selectedTutorial?.videoUrl ?? ''}
            />
        </>
    )
}

export const TutorialsPage = usePSComponent(render, viewWillAppear)
